import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { formatApiError } from '@/lib/api';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6" data-testid="login-logo-link">
            <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
            <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
          </Link>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-zinc-900">Welcome back</h1>
          <p className="font-body text-sm text-zinc-500 mt-1">Log in to access your dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-8" data-testid="login-form">
          {error && (
            <div className="bg-rose-50 border border-rose-200 px-4 py-3 mb-6" data-testid="login-error">
              <p className="font-body text-sm text-rose-700">{error}</p>
            </div>
          )}
          <div className="mb-5">
            <label className="block font-body text-sm font-medium text-zinc-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand focus:ring-1 focus:ring-brand px-4 py-2.5 font-body text-sm text-zinc-900 outline-none transition-colors"
              placeholder="you@company.com"
              data-testid="login-email-input"
            />
          </div>
          <div className="mb-6">
            <label className="block font-body text-sm font-medium text-zinc-700 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand focus:ring-1 focus:ring-brand px-4 py-2.5 font-body text-sm text-zinc-900 outline-none transition-colors"
              placeholder="Enter your password"
              data-testid="login-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand text-white hover:bg-brand-hover py-3 font-body font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging in...</> : 'Log In'}
          </button>
        </form>
        <p className="text-center mt-6 font-body text-sm text-zinc-500">
          Don't have an account?{' '}
          <Link to="/signup" className="text-brand font-medium hover:underline" data-testid="login-signup-link">Sign up</Link>
        </p>
      </div>
    </div>
  );
}
